import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FileError } from 'react-dropzone';
import dayjs from 'dayjs';

import { useFileConvertToLMNStore } from '@/components/elements/DropScan/stores';
import useFilesInDocumentStore from '@/components/elements/DropScan/useFilesInDocument.store';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import useFileUploadQuery from '@/modules/ReimburseMe/hooks/useFileUploadQuery';

import { LMN_UPLOAD_STORE_KEY, LMNPopupMode } from '../LettersOfMedicalNecessity.types';
import { useCreateLettersOfMedicalNecessityQuery } from '../queries';
import { LettersOfMedicalNecessityPopupState } from '../stores/useLettersOfMedicalNecessityPopup.store';

interface Props {
  onFileUploadError?: (error?: FileError) => void;
  onSuccess?: (id?: string) => void;
  mode?: LMNPopupMode;
}

const useSubmitLettersOfMedicalNecessityPopup = ({ onFileUploadError, mode, onSuccess }: Props) => {
  const isConvertMode = mode === LMNPopupMode.CONVERT;

  const { createDocumentId, uploadFiles } = useFileUploadQuery();
  const currentFileId = useFileConvertToLMNStore((state) => state.currentFileId);

  const selectIndexes = useFileConvertToLMNStore((state) => state.selectIndexes);

  const { createLMN } = useCreateLettersOfMedicalNecessityQuery({
    onSuccess,
  });
  const {
    values: { documentId, files },
    handleSetValues,
  } = useFilesInDocumentStore(LMN_UPLOAD_STORE_KEY);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const documentIdRef = useRef<string | undefined>(documentId);
  const filesRef = useRef<typeof files>(files);

  const file = useMemo(() => files?.[0], [files]);

  useEffect(() => {
    documentIdRef.current = documentId;
  }, [documentId]);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const handleCreateLMN = useCallback(
    async (payload: LettersOfMedicalNecessityPopupState) => {
      let createdDocumentId = documentIdRef.current || '';
      if (!createdDocumentId && !isConvertMode) {
        createdDocumentId = await createDocumentId(payload?.documentType);
        documentIdRef.current = createdDocumentId;
        handleSetValues({ documentId: createdDocumentId });
      }

      const currentFiles = filesRef.current || [];
      const unuploadedFiles = currentFiles.filter((file) => !file.id);
      if (unuploadedFiles?.length > 0) {
        const uploadedFiles = await uploadFiles(createdDocumentId, unuploadedFiles, false, onFileUploadError);
        if (!uploadedFiles?.length) {
          return;
        }
        const normalizedFiles = uploadedFiles?.filter((file) => file.id);
        filesRef.current = normalizedFiles;
        handleSetValues({ files: normalizedFiles });
      }
      const validFromRaw = payload?.validDates?.[0];
      const validToRaw = payload?.validDates?.[1];
      const hasValidDateRange = validFromRaw && validToRaw;

      const createdLMN = await createLMN({
        documentId: createdDocumentId,
        name: payload?.name,
        description: payload?.description,
        employeeId: payload?.employeeId,
        claimIds: payload?.claimIds,
        ...(isConvertMode && currentFileId
          ? {
              fileReassignment: {
                fileId: currentFileId,
                pages:
                  selectIndexes && selectIndexes.length > 0
                    ? selectIndexes.map((index) => index + 1)
                    : undefined,
              },
            }
          : {}),
        ...(hasValidDateRange
          ? {
              validFrom: validFromRaw ? dayjs(validFromRaw).toISOString() : undefined,
              validTo: validToRaw ? dayjs(validToRaw).toISOString() : undefined,
            }
          : {}),
      });
      return createdLMN;
    },
    [
      createLMN,
      isConvertMode,
      currentFileId,
      selectIndexes,
      createDocumentId,
      handleSetValues,
      uploadFiles,
      onFileUploadError,
    ]
  );

  const handleSuccessfullyCreated = useCallback(
    (createForClaimId?: string) => {
      handleAddPermanentSnackbar({
        text: createForClaimId
          ? 'Document added. The claim is about to be processed.'
          : `${file?.name} was added.`,
        state: createForClaimId ? SnackbarState.neutral : SnackbarState.positive,
        closeIcon: true,
        autoRemove: true,
      });
    },
    [handleAddPermanentSnackbar, file?.name]
  );

  return {
    handleCreateLMN,
    handleSuccessfullyCreated,
  };
};

export default useSubmitLettersOfMedicalNecessityPopup;
